/**
 * Marketing Template API Service
 * Handles all API calls related to marketing email templates
 */

import { ApiService } from './api.service';
import { endpoints } from './endpoints';
import type {
  IMarketingTemplate,
  ICreateTemplateDto,
  IUpdateTemplateDto,
  IMarketingTemplateItem,
  ITemplatePreviewRequest,
  ITemplatePreviewResponse,
  IMarketingTemplateResponse,
  IMarketingTemplatesResponse,
  ITemplateStatisticsResponse,
  IMarketingTemplateTableFilters,
} from '@/types';

/**
 * Marketing Template API Service Class
 */
export class MarketingTemplateApiService extends ApiService {
  /**
   * Get all templates (unpaginated)
   */
  async getAllTemplates(): Promise<IMarketingTemplateItem[]> {
    const response = await this.get<{ docs: IMarketingTemplateItem[] }>(endpoints.marketingTemplates.all);
    return response.docs;
  }

  /**
   * Get filtered and paginated templates
   */
  async getFilteredTemplates(
    filters: IMarketingTemplateTableFilters
  ): Promise<IMarketingTemplatesResponse['data']> {
    const params: Record<string, string | number | boolean> = {
      page: filters.page,
      pageSize: filters.pageSize,
    };

    if (filters.search) params.search = filters.search;
    if (filters.category) params.category = filters.category;
    if (filters.status) params.status = filters.status;
    if (filters.isActive !== undefined) params.isActive = filters.isActive;

    const response = await this.get<IMarketingTemplatesResponse['data']>(
      endpoints.marketingTemplates.filtered,
      { params }
    );
    return response;
  }

  /**
   * Get template by ID
   */
  async getTemplateById(id: string): Promise<IMarketingTemplate> {
    const response = await this.get<IMarketingTemplate>(
      endpoints.marketingTemplates.byId(id)
    );
    return response;
  }

  /**
   * Create new template
   */
  async createTemplate(data: ICreateTemplateDto): Promise<{ id: string }> {
    const response = await this.post<{ id: string }>(
      endpoints.marketingTemplates.create,
      data
    );
    return response;
  }

  /**
   * Update template
   */
  async updateTemplate(id: string, data: IUpdateTemplateDto): Promise<void> {
    await this.patch(endpoints.marketingTemplates.update(id), data);
  }

  /**
   * Delete template
   */
  async deleteTemplate(id: string): Promise<void> {
    await this.delete(endpoints.marketingTemplates.delete(id));
  }

  /**
   * Preview template with variables
   */
  async previewTemplate(id: string, variables: Record<string, string>): Promise<ITemplatePreviewResponse> {
    const response = await this.post<ITemplatePreviewResponse>(
      endpoints.marketingTemplates.preview(id),
      { variables }
    );
    return response;
  }

  /**
   * Duplicate template
   */
  async duplicateTemplate(id: string, newName: string): Promise<{ id: string }> {
    const response = await this.post<{ id: string }>(
      endpoints.marketingTemplates.duplicate(id),
      { name: newName }
    );
    return response;
  }

  /**
   * Get template statistics
   */
  async getTemplateStatistics(): Promise<ITemplateStatisticsResponse> {
    const response = await this.get<ITemplateStatisticsResponse>(
      endpoints.marketingTemplates.statistics
    );
    return response;
  }
}

/**
 * Singleton instance
 */
export const marketingTemplateApiService = new MarketingTemplateApiService();

/**
 * Delete template (standalone function for backward compatibility)
 */
export const deleteMarketingTemplate = async (id: string): Promise<void> => {
  return marketingTemplateApiService.deleteTemplate(id);
};

/**
 * Get template by ID (standalone function)
 */
export const getTemplateById = async (id: string): Promise<IMarketingTemplate> => {
  return marketingTemplateApiService.getTemplateById(id);
};

/**
 * Create template (standalone function)
 */
export const createTemplate = async (data: ICreateTemplateDto): Promise<{ id: string }> => {
  return marketingTemplateApiService.createTemplate(data);
};

/**
 * Update template (standalone function)
 */
export const updateTemplate = async (id: string, data: IUpdateTemplateDto): Promise<void> => {
  return marketingTemplateApiService.updateTemplate(id, data);
};
